import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner destacado que aparece en la pantalla principal del Dashboard
 * cuando la clienta tiene comentarios sin leer de la coach en sus fotos
 * de progreso.
 *
 * Reglas:
 *  - Sólo se muestra si la usuaria YA cambió su contraseña
 *    (props: canShow — el Dashboard pasa false cuando must_change_password=true).
 *  - Si hay múltiples mensajes acumulados se muestran uno por uno: al
 *    "abrir" uno con Ver mensaje, se marca ese individual como leído.
 *    La opción "Ver todos" abre ProgressPhotos y marca todos como leídos.
 *  - Cuando la usuaria toca "Ver mensaje" o "Ver todos", el banner se
 *    oculta inmediatamente en la UI y se marca en el backend.
 */
export default function CoachProgressCommentBanner({ user, canShow = true }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const getToken = () =>
    localStorage.getItem('deisycfit_token') ||
    localStorage.getItem('deisycfit_admin_token');

  const fetchItems = useCallback(async () => {
    if (!canShow) return;
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/user/progress-comments/unread`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.items || [];
      setItems(list);
      setCurrent(0);
      setDismissed(false);
    } catch {
      // silent — no bloqueamos el dashboard
    }
  }, [canShow]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const markOneAsRead = async (id) => {
    const token = getToken();
    if (!token || !id) return;
    try {
      await axios.put(
        `${API}/api/user/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* silent */ }
  };

  const markAllAsRead = async () => {
    const token = getToken();
    if (!token) return;
    try {
      await axios.put(
        `${API}/api/user/progress-comments/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* silent */ }
  };

  if (!canShow || dismissed || items.length === 0) return null;

  const currentItem = items[current];
  const remaining = items.length - current;
  const hasMore = remaining > 1;

  const handleOpenPhotos = async () => {
    setLoading(true);
    await markAllAsRead();
    setDismissed(true);
    setLoading(false);
    navigate('/my-progress');
  };

  const handleNext = async () => {
    if (!currentItem?.id) return;
    setLoading(true);
    await markOneAsRead(currentItem.id);
    setLoading(false);
    if (current + 1 >= items.length) {
      setDismissed(true);
    } else {
      setCurrent((c) => c + 1);
    }
  };

  return (
    <div
      className="rounded-2xl p-4 md:p-5 mb-4 flex items-start gap-3 md:gap-4"
      style={{
        background:
          'linear-gradient(135deg, rgba(236,72,153,0.18), rgba(168,85,247,0.14))',
        border: '1.5px solid rgba(236,72,153,0.55)',
        boxShadow: '0 8px 40px rgba(236,72,153,0.20)',
      }}
      data-testid="coach-progress-banner"
    >
      <div className="text-3xl md:text-4xl leading-none pt-0.5">💌</div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold mb-1">
          Mensaje de tu coach
        </p>
        <h3
          className="text-base md:text-lg font-black text-white mb-1"
          style={{ fontFamily: 'Poppins, sans-serif' }}
          data-testid="coach-progress-banner-title"
        >
          Tu coach te dejó un mensaje en tus fotos de progreso
        </h3>
        {currentItem?.preview && (
          <p
            className="text-xs md:text-sm text-white/85 mb-2 line-clamp-2"
            data-testid="coach-progress-banner-preview"
          >
            &ldquo;{currentItem.preview}&rdquo;
          </p>
        )}
        {hasMore && (
          <p className="text-[11px] text-pink-200 mb-2">
            Tienes <strong>{remaining}</strong> mensajes acumulados
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-1">
          <button
            type="button"
            onClick={handleOpenPhotos}
            disabled={loading}
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest text-white transition-all disabled:opacity-60"
            style={{
              background:
                'linear-gradient(135deg, rgb(236,72,153), rgb(168,85,247))',
              boxShadow: '0 4px 16px rgba(236,72,153,0.35)',
            }}
            data-testid="coach-progress-banner-open"
          >
            Ver mis fotos y respuesta →
          </button>
          {hasMore && (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading}
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-white/85 border border-white/20 hover:border-white/40 transition-all disabled:opacity-60"
              data-testid="coach-progress-banner-next"
            >
              Siguiente mensaje ({current + 1}/{items.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
